import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  AuthStatus,
  ChangePasswordPayload,
  ChangePasswordResult,
  LoginCredentials,
  RegisterPayload,
  RegisterResult,
  User,
} from "@astro/types";
import { setUnauthorizedHandler } from "@astro/api";
import { createStore } from "@astro/storage";
import { authClient } from "./client";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  /** Email + password sign-in against the auth BFF. Resolves to the user. */
  loginWithPassword: (credentials: LoginCredentials) => Promise<User>;
  /** WhatsApp OTP sign-in: exchange a verified mobile+otp pair. Resolves to the user. */
  loginWithOtp: (mobile: string, otp: string) => Promise<User>;
  /** Create an account; auto-signs-in when the API returns a token. */
  register: (payload: RegisterPayload) => Promise<RegisterResult>;
  /** Re-key the password of the already-signed-in user. Keeps this session
   * (the BFF re-seals the cookie) while other devices are signed out. */
  changePassword: (payload: ChangePasswordPayload) => Promise<ChangePasswordResult>;
  /** Redirect to the central login page (used by apps without a local /login). */
  login: (returnTo?: string, opts?: { replace?: boolean }) => void;
  /** Global logout: full-page redirect to the BFF /logout (clears the shared cookie, revokes the token). */
  logout: () => void;
  refresh: () => Promise<boolean>;
  /** Re-read the session user (e.g. after a profile photo change) so the
   * headers pick it up without a page reload. */
  reloadSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Non-sensitive last-known profile, cached in localStorage so returning users
 * see their identity instantly while the cookie session is re-validated.
 * The token itself never touches the browser — it lives in the BFF cookie.
 */
const userCache = createStore<User>("auth:user");

/**
 * Wraps every app root. On mount it performs a silent SSO check against the
 * BFF; if the shared parent-domain cookie is present, the user is already
 * authenticated with zero interaction.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => userCache.get() ?? null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    let active = true;

    // Let api-client trigger a silent session re-validation on any 401.
    setUnauthorizedHandler(async () => authClient.refresh());

    authClient
      .session()
      .then((session) => {
        if (!active) return;
        if (session) {
          setUser(session.user);
          userCache.set(session.user);
          setStatus("authenticated");
        } else {
          setUser(null);
          userCache.remove();
          setStatus("unauthenticated");
        }
      })
      .catch(() => {
        if (!active) return;
        setStatus("unauthenticated");
      });

    return () => {
      active = false;
    };
  }, []);

  const loginWithPassword = useCallback(async (credentials: LoginCredentials) => {
    const session = await authClient.loginWithPassword(credentials);
    setUser(session.user);
    userCache.set(session.user);
    setStatus("authenticated");
    return session.user;
  }, []);

  const loginWithOtp = useCallback(async (mobile: string, otp: string) => {
    const session = await authClient.loginWithOtp(mobile, otp);
    setUser(session.user);
    userCache.set(session.user);
    setStatus("authenticated");
    return session.user;
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const result = await authClient.register(payload);
    if (result.user && !result.requiresLogin) {
      setUser(result.user);
      userCache.set(result.user);
      setStatus("authenticated");
    }
    return result;
  }, []);

  const reloadSession = useCallback(async () => {
    const session = await authClient.session().catch(() => null);
    if (!session) return;
    setUser(session.user);
    userCache.set(session.user);
  }, []);

  const logout = useCallback(() => {
    // Only purge the cached identity — do NOT flip status/user here.
    // authClient.logout() is a full-page navigation to the BFF /logout, but
    // the document keeps running until it commits; setting status to
    // "unauthenticated" first re-renders RequireAuth on guarded pages (e.g.
    // /dashboard), whose login redirect then cancels the pending /logout
    // navigation — the cookie is never cleared and the user bounces back in.
    userCache.remove();
    authClient.logout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      loginWithPassword,
      loginWithOtp,
      register,
      changePassword: (payload) => authClient.changePassword(payload),
      login: (returnTo, opts) => authClient.login(returnTo, opts),
      logout,
      refresh: () => authClient.refresh(),
      reloadSession,
    }),
    [user, status, loginWithPassword, loginWithOtp, register, logout, reloadSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
